/**
 * WHERE AN ISLAND SITS RELATIVE TO ITS PLACE, so that the route passes CLEAR of its body.
 *
 * CONTRACT 1.5n(b): "THE ROUTE IS RE-LAID SO IT THREADS BETWEEN ISLANDS RATHER THAN THROUGH
 * THEM. Placement stops being a decoration applied after the track exists and becomes part of
 * laying the track. Note this cuts against `placeIslets` as written, which applies **no** track
 * clearance at all and whose fixed-north offset moves 11 of 20 islands *toward* the track rather
 * than away."
 *
 * MEASURED, on the shipped layout, before this file existed: of the 20 charted places, the fixed
 * north offset moved ELEVEN toward the track and nine away, and left place-01's islet 0.419
 * leagues from a track whose own islet is 2.54 leagues to its own edge. That is not a clearance
 * rule failing at the margin; it is no clearance rule at all, applied in a direction that has
 * nothing to do with where the track is.
 *
 * -- THE RULE, AND WHY IT LIVES IN THE VOYAGE LAYER ---------------------------
 *
 * The offset is RADIAL, because the track is a spiral whose coils are separated radially by
 * TRACK_COIL_SPACING_LEAGUES; moving an island along the radius is the only direction that buys
 * clearance from the coil it is nearest. The island is set at its own radial half-extent plus
 * `BERTH_CLEARANCE_LEAGUES` from THE PLACE POINT, pushed to whichever side of the nearest coil the
 * place point already lies on. So the size of the island decides how far it moves - the old
 * constant could not know how big the sprite was - and the boat, which is made fast at the place
 * point, lies exactly the clearance off the island's near edge.
 *
 * It lives HERE and not in `src/render/islets.ts` because the rule is a fact about the ROUTE.
 * The renderer applies it to a sprite and `places.ts` applies it to a published extent, and
 * `src/render/islets.test.ts` fails if the two ever disagree - which is what keeps the picture
 * and the simulation talking about the same island.
 *
 * -- WHAT IT CANNOT DO, STATED ------------------------------------------------
 *
 * An island wider than the coil spacing CANNOT be berthed clear of the track, in any direction,
 * at any offset: at 20.11 leagues between coils a body 39 to 78 leagues across (CONTRACT 1.5h(b))
 * spans two to four of them wherever it is put. `berthOffsetLeagues` returns the best offset it
 * can and `berthClears` says plainly whether that offset actually clears the track. For a body
 * that cannot be cleared the answer is not a bigger offset; it is CONTRACT 1.5n(a), the boat
 * moors - see `calls.ts`.
 */

import { SPIRAL_LEAGUES_PER_RADIAN, VOYAGE_GEOMETRY } from './constants';
import type { Leagues, Vec2 } from './units';
import { vec2 } from './units';

const TWO_PI = 2 * Math.PI;

/**
 * Water kept between an island's drawn edge and the boat lying at its place point. MODELLED and
 * derived rather than literal: an eighth of a frame width, 3 leagues, 15.4 m, a hull and a half.
 * The boat lies that far off the beach when it is made fast, and the same figure is the least
 * water between the island and the track, because the island is pushed to the far side of the
 * place point from the coil. The boat passes close along a coast on purpose - CONTRACT 1.5h(b),
 * "you sail along a coast rather than past a lump".
 */
export const BERTH_CLEARANCE_LEAGUES: Leagues = VOYAGE_GEOMETRY.frameWidthLeagues / 8;

/**
 * The signed radial gap from a point to the nearest coil of the track: positive seaward of that
 * coil, negative toward home from it, zero on it.
 *
 * `src/render/islets.ts` `distanceToTrack` is the magnitude of this and is kept, because the
 * field scatter only ever asks how far. The SIGN is what the berth rule needs: an island is moved
 * further out on the side it is already on, so a place point never crosses the track to reach its
 * own island.
 *
 * At r near zero every coil is the same coil and there is no meaningful side; the function
 * returns 0 there and `berthOffsetLeagues` declines to move anything, which is correct - home is
 * where the spiral is born and there is no gap to berth in.
 */
export function signedTrackGapLeagues(x: Leagues, y: Leagues): Leagues {
  const A = SPIRAL_LEAGUES_PER_RADIAN;
  const r = Math.hypot(x, y);
  if (!(r > 1e-9)) return 0;
  let phi = Math.atan2(x, y);
  if (phi < 0) phi += TWO_PI;
  const k0 = Math.round((r / A - phi) / TWO_PI);
  let best = 0;
  let bestAbs = Infinity;
  for (let k = k0 - 1; k <= k0 + 1; k += 1) {
    const theta = phi + TWO_PI * k;
    if (theta < 0) continue;
    const d = r - A * theta;
    const a = Math.abs(d);
    if (a < bestAbs) {
      bestAbs = a;
      best = d;
    }
  }
  return Number.isFinite(bestAbs) ? best : 0;
}

/** An island's box, as the berth rule needs it: half-extents in leagues about its own centre. */
export interface BodyHalfExtent {
  readonly halfWidthLeagues: Leagues;
  readonly halfHeightLeagues: Leagues;
}

/**
 * How far the island's box reaches along the radius at this point. The box is axis-aligned in
 * world space and the radius is not, so the support is the box's own width projected onto it.
 */
export function radialSupportLeagues(point: Vec2, body: BodyHalfExtent): Leagues {
  const r = Math.hypot(point.x, point.y);
  if (!(r > 1e-9)) return Math.max(body.halfWidthLeagues, body.halfHeightLeagues);
  const nx = Math.abs(point.x) / r;
  const ny = Math.abs(point.y) / r;
  return nx * body.halfWidthLeagues + ny * body.halfHeightLeagues;
}

/**
 * THE OFFSET, in leagues, from a place point to the CENTRE of the island drawn for it.
 *
 * Radial, sized by the island's own radial support plus `clearance`, pushed AWAY from the nearest
 * coil - which is the side of that coil the place point is already on. Returns the zero vector at
 * the origin, where there is no side to be on.
 *
 * IT IS MEASURED FROM THE PLACE POINT, NOT FROM THE COIL, AND THE FIRST VERSION OF THIS FUNCTION
 * WAS MEASURED FROM THE COIL. That version set the island's centre at a fixed radial offset from
 * the track, and a place point already 5.4 leagues out from its coil then landed INSIDE its own
 * island: the penetration instrument went from 21 leagues of the route on land to 883, because the
 * boat moors at the place point and the point was on the beach. Measuring from the point makes the
 * clear water between the boat and the island's near edge exactly `clearance` by construction, and
 * the clear water between the island and the track at least that much, because the point is on the
 * far side of the coil from the island.
 */
export function berthOffsetLeagues(
  point: Vec2,
  body: BodyHalfExtent,
  clearance: Leagues = BERTH_CLEARANCE_LEAGUES,
): Vec2 {
  const r = Math.hypot(point.x, point.y);
  if (!(r > 1e-9)) return vec2(0, 0);
  const nx = point.x / r;
  const ny = point.y / r;
  const gap = signedTrackGapLeagues(point.x, point.y);
  const side = gap < 0 ? -1 : 1;
  const move = side * (radialSupportLeagues(point, body) + clearance);
  return vec2(nx * move, ny * move);
}

/** The island's centre once berthed: the place point plus the offset. */
export function berthCentreLeagues(
  point: Vec2,
  body: BodyHalfExtent,
  clearance: Leagues = BERTH_CLEARANCE_LEAGUES,
): Vec2 {
  const off = berthOffsetLeagues(point, body, clearance);
  return vec2(point.x + off.x, point.y + off.y);
}

/**
 * Whether the berth actually clears the track, which is a question about the island's SIZE and
 * not about the offset. False for any body whose radial support plus the clearance reaches past
 * the halfway point between two coils, because then both coils are inside it.
 */
export function berthClears(
  point: Vec2,
  body: BodyHalfExtent,
  clearance: Leagues = BERTH_CLEARANCE_LEAGUES,
): boolean {
  const r = Math.hypot(point.x, point.y);
  if (!(r > 1e-9)) return false;
  const coil = TWO_PI * SPIRAL_LEAGUES_PER_RADIAN;
  // The far edge of the berthed island, measured from the coil it was pushed away from, has to
  // stop short of the NEXT coil: the place point's own gap, plus the whole body, plus the water
  // kept between the body and the coil behind it.
  const gap = Math.abs(signedTrackGapLeagues(point.x, point.y));
  return gap + 2 * radialSupportLeagues(point, body) + clearance < coil;
}
